import { useState, useEffect } from 'react';
import { Box, Text, Flex, Badge } from '@radix-ui/themes';
import { ActionButton, ButtonGroup } from './StyledComponents';
import TextInput from '../../shared/FormStyles/TextInput.jsx';
import SectionCard from '../../shared/SectionCard/SectionCard.jsx';
import DeleteComponent from '../../shared/SettingsModal/DeleteComponent.jsx';
import { Modal } from '../boardSetup/components/Modals/Modal.jsx';
import useGetAccountSettings from './hooks/useGetAccountSettings';
import useInviteDependent, {
  useResendDependentInvite,
} from './hooks/useInviteDependent';
import useRemoveDependent from './hooks/useRemoveDependent';

const ExistingDependentInfo = ({ dependent }) => {
  const resendMutation = useResendDependentInvite();
  const removeMutation = useRemoveDependent();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleRemove = () => {
    removeMutation.mutate(undefined, {
      onSuccess: () => setShowConfirmation(false),
    });
  };

  const confirmationMessage = dependent.accepted
    ? `Are you sure you want to remove ${dependent.name ||
        dependent.email} as your dependent? Their account will be permanently deleted.`
    : `Are you sure you want to cancel the pending invitation to ${dependent.email}?`;

  return (
    <SectionCard title="Dependent">
      <Box mt="2" pb="4">
        <Flex direction="column" gap="3">
          <Flex align="center" gap="1">
            <Text size="2">Name: </Text>
            <Text size="2" weight="medium">
              {dependent.name || 'N/A'}
            </Text>
          </Flex>

          <Flex align="center" gap="1">
            <Text size="2">Email: </Text>
            <Text size="2" weight="medium">
              {dependent.email}
            </Text>
          </Flex>

          <Flex align="center" gap="1">
            <Text size="2">Status: </Text>
            <Badge color={dependent.accepted ? 'green' : 'orange'}>
              {dependent.accepted ? 'Accepted' : 'Pending'}
            </Badge>
          </Flex>
        </Flex>

        <ButtonGroup>
          {!dependent.accepted && (
            <ActionButton
              variant="containedBlue"
              onClick={() => resendMutation.mutate(dependent.id)}
              disabled={resendMutation.isPending}
            >
              {resendMutation.isPending ? 'Sending...' : 'Resend Invitation'}
            </ActionButton>
          )}
          <ActionButton
            variant="tertiary"
            onClick={() => setShowConfirmation(true)}
            disabled={removeMutation.isPending}
          >
            {removeMutation.isPending ? 'Removing...' : 'Remove'}
          </ActionButton>
        </ButtonGroup>
      </Box>

      <Modal open={showConfirmation} onClose={() => setShowConfirmation(false)}>
        <DeleteComponent
          phrases={[confirmationMessage]}
          onConfirm={handleRemove}
          onCancel={() => setShowConfirmation(false)}
        />
      </Modal>
    </SectionCard>
  );
};

const InviteForm = () => {
  const {
    dependentBoardEmailSubject,
    dependentBoardEmailBody,
  } = useGetAccountSettings();
  const inviteMutation = useInviteDependent();

  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  useEffect(() => {
    if (dependentBoardEmailSubject) setSubject(dependentBoardEmailSubject);
    if (dependentBoardEmailBody) setBody(dependentBoardEmailBody);
  }, [dependentBoardEmailSubject, dependentBoardEmailBody]);

  const handleSubmit = e => {
    e.preventDefault();
    inviteMutation.mutate(
      { email, subject, body },
      { onSuccess: () => setEmail('') },
    );
  };

  return (
    <SectionCard title="Invite Dependent">
      <Box mt="2" pb="4">
        <Text as="p" size="2" color="gray" mb="4">
          Invite a dependent to join your benefits portal.
        </Text>

        <form onSubmit={handleSubmit}>
          <TextInput
            value={email}
            onChange={e => setEmail(e.target.value)}
            name="email"
            type="email"
            label="Email Address *"
            placeholder="dependent@email.com"
            m="0 0 24px"
            required
          />
          <TextInput
            value={subject}
            onChange={e => setSubject(e.target.value)}
            name="subject"
            label="Subject *"
            m="0 0 24px"
            required
          />
          <TextInput
            value={body}
            onChange={e => setBody(e.target.value)}
            name="body"
            label="Message *"
            m="0 0 24px"
            required
          />
          <ButtonGroup>
            <ActionButton
              type="submit"
              variant="containedBlue"
              disabled={inviteMutation.isPending}
            >
              {inviteMutation.isPending ? 'Sending...' : 'Send Invitation'}
            </ActionButton>
          </ButtonGroup>
        </form>
      </Box>
    </SectionCard>
  );
};

// canInviteDependents (checked by the parent tab) says the feature applies to
// this user; canAddDependent says there is room for one more. An account holds
// a single dependent, so the form only shows while there is none.
const InviteDependent = () => {
  const { existingDependent, canAddDependent } = useGetAccountSettings();

  if (existingDependent) {
    return <ExistingDependentInfo dependent={existingDependent} />;
  }

  if (!canAddDependent) return null;

  return <InviteForm />;
};

export default InviteDependent;
